import { defineComponent as _defineComponent } from 'vue'
import { Customer } from '@/models/tenant';
import EmailsTagInput from '@/components/inputs/EmailsTagInput.vue';
import { customersService } from '@/services/tenants.service';
import { User } from '@/models/user';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import { isEmpty } from '@/utils/misc';
import { CustomerReport } from '@/models/customerReport';
import { EmailEntry } from '@/models/EmailEntry';
import { computed, onMounted, ref } from 'vue';
import { LoadingProgrammatic } from 'buefy';
import { toastService } from '@/library-services/toast.service';
import { useI18n } from '@/composables/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerEmailReport',
  props: {
    customer: null
  },
  emits: ["update"],
  setup(__props: any, { emit }: { emit: ((e: 'update', value: Customer) => void), expose: any, slots: any, attrs: any }) {

const props = __props as {
  customer: Customer;
};





const emailsTagInput = ref<InstanceType<typeof EmailsTagInput>>();
const hasDailyReport = ref(false);
const hasWeeklyReport = ref(false);
const hasMonthlyReport = ref(false);
const hasQuarterlyReport = ref(false);
const hasAnnualReport = ref(false);
const originalEmails = ref<string[]>([]);
const selectedEmails = ref<string[]>([]);

const isSubmitting = ref(false);
const { t } = useI18n();

onMounted(() => {
  initializeValues();
  const loading = LoadingProgrammatic.open({
    container: emailsTagInput.value?.$el,
  });
  customersService
    .getReportUsers(props.customer.id)
    .then((users) => {
      const emails = users.map((user) => user.email);
      originalEmails.value = [...emails];
      selectedEmails.value = [...emails];
    })
    .finally(() => loading.close());
});

function onEmailInput(emails: EmailEntry[]) {
  selectedEmails.value = [...emails.map((email) => email.email)];
}

function initializeValues() {
  hasDailyReport.value = props.customer.hasDailyReport;
  hasWeeklyReport.value = props.customer.hasWeeklyReport;
  hasMonthlyReport.value = props.customer.hasMonthlyReport;
  hasQuarterlyReport.value = props.customer.hasQuarterlyReport;
  hasAnnualReport.value = props.customer.hasAnnualReport;
  selectedEmails.value = [...originalEmails.value];
}

function cancel() {
  initializeValues();
  emailsTagInput.value?.initializeTag();
}

async function save() {
  isSubmitting.value = true;
  try {
    const selectedEmailsToSave = noReportActivated.value
      ? []
      : selectedEmails.value;
    await customersService.updateReport(
      new CustomerReport(
        props.customer.id,
        hasDailyReport.value,
        hasWeeklyReport.value,
        hasMonthlyReport.value,
        hasQuarterlyReport.value,
        hasAnnualReport.value,
        selectedEmailsToSave,
      ),
    );
    toastService.success('tenant.update_success');
    if (noReportActivated.value) {
      selectedEmails.value = [];
    }
    originalEmails.value = [...selectedEmailsToSave];
    props.customer.hasDailyReport = hasDailyReport.value;
    props.customer.hasWeeklyReport = hasWeeklyReport.value;
    props.customer.hasMonthlyReport = hasMonthlyReport.value;
    props.customer.hasQuarterlyReport = hasQuarterlyReport.value;
    props.customer.hasAnnualReport = hasAnnualReport.value;
    await customersService.store.update(props.customer);
    emit('update', props.customer);
  } finally {
    isSubmitting.value = false;
  }
}

function filterUsersWithoutWCC(user: User): boolean {
  return user.consoles.includes(ConsoleEnum.WCC) || user.isGlobalAdmin;
}

const noReportActivated = computed((): boolean => {
  return !(
    hasDailyReport.value ||
    hasWeeklyReport.value ||
    hasMonthlyReport.value ||
    hasQuarterlyReport.value ||
    hasAnnualReport.value
  );
});

const changesOccurred = computed((): boolean => {
  const smallerList =
    selectedEmails.value.length < originalEmails.value.length
      ? selectedEmails.value
      : originalEmails.value;
  const largerList =
    selectedEmails.value.length >= originalEmails.value.length
      ? selectedEmails.value
      : originalEmails.value;
  // Low complexity email cross check
  const emailHash = Object.fromEntries(smallerList.map((e) => [e, 1]));
  let emailsHaveChanged = false;
  for (const e of largerList) {
    if (!(e in emailHash)) {
      emailsHaveChanged = true;
      break;
    }
  }
  return (
    props.customer.hasDailyReport !== hasDailyReport.value ||
    props.customer.hasWeeklyReport !== hasWeeklyReport.value ||
    props.customer.hasMonthlyReport !== hasMonthlyReport.value ||
    props.customer.hasQuarterlyReport !== hasQuarterlyReport.value ||
    props.customer.hasAnnualReport !== hasAnnualReport.value ||
    emailsHaveChanged
  );
});

const formIsValid = computed(() => {
  if (noReportActivated.value) {
    return true;
  } else {
    return !isEmpty(selectedEmails.value);
  }
});

return { __sfc: true,props, emit, emailsTagInput, hasDailyReport, hasWeeklyReport, hasMonthlyReport, hasQuarterlyReport, hasAnnualReport, originalEmails, selectedEmails, isSubmitting, t, onEmailInput, initializeValues, cancel, save, filterUsersWithoutWCC, noReportActivated, changesOccurred, formIsValid, EmailsTagInput }
}

})